// CartonSheet.jsx — one carton, opened from its gear on the Cartons screen
// (V2 #100). Its details, its files, and the one change to make: Unshare,
// Delete or Restore, with a reason the backend keeps with the change (#99).
// The sheet closes on save, and the table reloads.

import React from 'react';
import { api, errorText } from '../api';
import { stamp } from '../format';
import { useToast } from './Toast';
import { Button, Field, KV, Note, Pill, Sheet } from './primitives';
import {
  ACTIONS, STATUS_WORDS, actionProblem, actionsFor, duplicatesLabel, filesOf, kindLabel,
  sizeLabel, statusOf,
} from '../cartons';

function FileButtons({ carton, file }) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  const name = `${carton.name}${file.which === 'die' ? '' : '.pdf'}`.replace(/\.pdf\.pdf$/i, '.pdf');

  async function fetchUrl() {
    const blob = await api.cartonFile(carton.id, file.which);
    return URL.createObjectURL(blob);
  }
  async function open() {
    // Opened at the click, filled once the file arrives: a window opened
    // after an await is a pop-up the browser blocks.
    const w = window.open('', '_blank');
    setBusy(true);
    try {
      const url = await fetchUrl();
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) {
      if (w) w.close();
      toast.error(errorText(e));
    } finally { setBusy(false); }
  }
  async function download() {
    setBusy(true);
    try {
      const url = await fetchUrl();
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errorText(e));
    } finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13 }}>{file.label}</span>
      <span className="row" style={{ gap: 8 }}>
        {file.which === 'source' && <Button small disabled={busy} onClick={open}>Open</Button>}
        <Button small disabled={busy} onClick={download}>Download</Button>
      </span>
    </div>
  );
}

export default function CartonSheet({ carton, onClose, onDone }) {
  const toast = useToast();
  const choices = actionsFor(carton);
  const [action, setAction] = React.useState(choices.length === 1 ? choices[0] : null);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(null);
  const status = STATUS_WORDS[statusOf(carton)];
  const files = filesOf(carton);
  const problem = actionProblem(action, reason);

  async function save() {
    if (problem) return;
    setBusy(true);
    setFailed(null);
    try {
      await api.cartonAction(carton.id, action, reason);
      toast.ok(`${ACTIONS[action].done}: ${carton.name}`);
      onDone();
    } catch (e) {
      setFailed(errorText(e));
    } finally { setBusy(false); }
  }

  return (
    <Sheet title={carton.name}
           subtitle={[carton.owner_name, carton.org_name].filter(Boolean).join(' · ')}
           onClose={onClose}
           footer={(
             <>
               <Button onClick={onClose} disabled={busy}>Cancel</Button>
               <Button variant={action === 'delete' ? 'danger' : 'primary'} loading={busy}
                       disabled={!!problem} onClick={save}>
                 {action ? ACTIONS[action].label : 'Save'}
               </Button>
             </>
           )}>
      <div className="stack">
        <KV items={[
          ['Organisation', carton.org_name],
          ['Owner', carton.owner_name],
          ['Status', <Pill key="status" tone={status.tone}>{status.label}</Pill>],
          ['Kind', kindLabel(carton)],
          ['Size', sizeLabel(carton)],
          ['Added', stamp(carton.created_at)],
          ['Duplicates', duplicatesLabel(carton) || 'None'],
          carton.deleted_at && ['Deleted', stamp(carton.deleted_at)],
        ]} />

        {files.length > 0 && (
          <div className="stack" style={{ gap: 8 }}>
            {files.map((f) => <FileButtons key={f.which} carton={carton} file={f} />)}
          </div>
        )}
        {carton.deleted_at && (
          <div className="dim" style={{ fontSize: 12.5 }}>
            Its files are kept. Restore the carton to open them.
          </div>
        )}

        <Field label="What to do">
          <div className="stack" style={{ gap: 8 }}>
            {choices.map((a) => (
              <label key={a} className="cartons-choice">
                <input type="radio" name="carton-action" checked={action === a}
                       disabled={busy} onChange={() => setAction(a)} />
                <span>
                  <span className="cartons-choice-label">{ACTIONS[a].label}</span>
                  <span className="dim">{ACTIONS[a].says}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>

        <Field label="Reason" hint="Kept with the change, with your name and the time.">
          <textarea className="input cartons-reason" value={reason} disabled={busy} maxLength={500}
                    placeholder="Why this change?"
                    onChange={(e) => setReason(e.target.value)} />
        </Field>

        {failed && <Note tone="danger" title="Not changed">{failed}</Note>}
        {!failed && problem && action && <div className="dim" style={{ fontSize: 12 }}>{problem}</div>}
      </div>
    </Sheet>
  );
}
